import {FieldInputProps} from 'formik';
import {uniqBy} from 'lodash-es';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {Pot} from '../../../schema-types/Pot';
import {CASH_OBJ, isSelectableSourcePlanStatus} from '../../../utils/potsFilter';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {contributeFieldType} from './contributeFields';
import {includesSaltus} from '../../../utils/includesSaltus';
import {
  changeContributeFromFactory,
  changeContributeToFactory,
  getAllContributePots,
  loadSavedContribution,
} from '../hooks/contributeHooks';

export {
  changeContributeFromFactory,
  changeContributeToFactory,
  getAllContributePots,
  loadSavedContribution,
};

export const contributionSources = ({
  contributeFromPots,
  contributeTo,
  isSppDa,
}: {
  contributeFromPots: Pot[];
  contributeTo?: string;
  isSppDa: boolean;
}): Pot[] => {
  const filteredPots = contributeFromPots.filter((el) => {
    if (el.id === CASH_OBJ.id) {
      return true;
    }
    // All non-saltus plans in contribution sources should be hidden. Only show new cash contribution or Saltus plans.
    if (isSppDa && !el.isSaltus) {
      return false;
    }

    /**
     * removing a GIA Gross from sources if its parent is the selected destination
     */
    if (!!contributeTo && !!el.parent?.id && el.parent.id === contributeTo) {
      return false;
    }

    /**
     * we don't want the new shell plans appearing as possible source plans for
     * contribution recommendations.
     * No curoId implies this plan is a new shell plan.
     *
     * See more in jira ticket https://saltus.atlassian.net/browse/ON-4045
     */
    if (!el.curoId) {
      return false;
    }

    /**
     * Inactive plans (e.g. Lapsed, Surrendered, Transferred) must not be offered
     * as contribution sources. The contribution flow reads the destination payload
     * (`requirementPots(isSource: false)`), which is not filtered to active plans on
     * the backend, so we filter here. We deliberately filter client-side rather than
     * switching the source picker back to `sourcePots`: that payload flip was reverted
     * by ON-9168, so a status filter is the payload-independent fix.
     *
     * See https://saltus.atlassian.net/browse/ON-9350 (and ON-9097 / ON-9168).
     */
    if (!isSelectableSourcePlanStatus(el.planStatus)) {
      return false;
    }

    return true;
  });

  return uniqBy(filteredPots, 'id');
};
export const contributionDestinations = ({
  sourcePots,
  contributeFieldObj,
  recommendDetails,
  isSPPDA,
}: {
  sourcePots: Pot[];
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
  recommendDetails: OVRecommendation | null;
  isSPPDA: boolean;
}): Pot[] => {
  const savedInRecc =
    recommendDetails?.pots
      ?.filter((x) => x.planType === 'destination' && !x.pot.parent?.id)
      .map((x) => x.pot) || [];
  const destinations = uniqBy([...sourcePots, ...savedInRecc], 'id');

  return destinations.filter((el) => {
    if (contributeFieldObj.value?.contributeFrom?.id === el.id) {
      return false;
    }
    if (recommendDetails && el.isDummy && el.originalOvrId === recommendDetails.id) {
      return false; // this means it's a new plan, covered in new plan section
    }
    if (isSPPDA && !el.isSaltus) {
      return false;
    }
    return true;
  });
};

export const getDisableChangingPlan = (
  recommendDetails: OVRecommendation | null
): boolean => {
  const savedInRecc =
    recommendDetails?.pots?.filter(
      (x) => x.planType === 'destination' && !x.pot.parent?.id
    ) || [];
  return savedInRecc.length > 0;
};
export function getPlanTypeFromPot(pot: Pot): 'isa' | 'gia' | 'bond' | 'pension' | null {
  const name = pot.productName?.formattedName?.toLocaleLowerCase();
  if (name?.includes('isa')) {
    return 'isa';
  }
  if (name?.includes('bond')) {
    return 'bond';
  }
  if (name?.includes('gia')) {
    return 'gia';
  }
  if (name?.includes('pension')) {
    return 'pension';
  }
  return null;
}

export const contributionIsActuallyTransfer = ({
  values,
  pots,
}: {
  values: recommendFieldsType;
  pots: Pot[];
}) => {
  /*
  Add in “specie” and “full redemption” to contribution source panel if:
- ISA to ISA
- OR GIA to GIA
- OR BOTH provider is Saltus
    - AND ISA to GIA OR GIA to ISA
- OR if BOTH providers are the same
    - for ANY PLAN TYPES (AKA isa to bond, gia to pension, all of them)
  */
  if (
    values.contribute &&
    values.contribute?.contributeFrom &&
    values.contribute?.contributeTo
  ) {
    const fromPot = pots.find((x) => x.id === values.contribute!.contributeFrom!.id)!;
    const toPot = pots.find((x) => x.id === values.contribute!.contributeTo)!;

    if (!fromPot || !toPot) {
      return false;
    }
    const fromPotType = getPlanTypeFromPot(fromPot);
    const toPotType = getPlanTypeFromPot(toPot);
    if (fromPotType === toPotType) {
      return true;
    }
    if (
      fromPot.productProvider &&
      toPot.productProvider &&
      fromPot.productProvider.id === toPot.productProvider?.id
    ) {
      if (
        includesSaltus(fromPot.productProvider?.name) &&
        includesSaltus(toPot.productProvider?.name)
      ) {
        if (fromPotType === 'gia' && toPotType === 'isa') {
          return true;
        }
        if (fromPotType === 'isa' && toPotType === 'gia') {
          return true;
        }
        return false;
      }
      // not saltus, always a transfer
      return true;
    }
  }
  return false;
};

export const shouldShowEmployerContribution = ({
  isMinor,
  mainPlanType,
}: {
  isMinor: boolean | undefined;
  mainPlanType: string | undefined;
}): boolean => {
  if (mainPlanType === 'pension') {
    return !isMinor;
  }
  return false;
};
